import { useState } from 'react';
import { toast } from 'sonner';
import type { DocumentRenderTemplate } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { assertToolSucceeded } from '@/api/templates-api';
import { useAddTemplateSection } from '@/hooks/useTemplateQueries';
import { Plus } from 'lucide-react';

interface TemplateAddSectionFooterProps {
  template: DocumentRenderTemplate;
  definitionRootId: number;
}

// Outline-backed drafts pick from the backend's uncovered blocks; freeform drafts take a
// hand-written skeleton. Placement, coverage and validation all stay server-side.
export function TemplateAddSectionFooter({ template, definitionRootId }: TemplateAddSectionFooterProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [guidance, setGuidance] = useState('');
  const [skeleton, setSkeleton] = useState('');
  const [pickedBlockIds, setPickedBlockIds] = useState<number[]>([]);
  const addSection = useAddTemplateSection();

  const draft = template.draft;
  if (draft === undefined) return null;
  const outlineBacked = draft.outlineBacked;
  const uncovered = draft.uncoveredBlocks;

  const reset = () => {
    setTitle('');
    setGuidance('');
    setSkeleton('');
    setPickedBlockIds([]);
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      assertToolSucceeded(
        await addSection.mutateAsync({
          definitionDocumentId: definitionRootId,
          title: title.trim(),
          guidance: guidance.trim().length > 0 ? guidance.trim() : 'Added on the template page.',
          ...(outlineBacked
            ? { blockIds: [...pickedBlockIds].sort((a, b) => a - b) }
            : { skeleton: skeleton.trim() }),
        })
      );
      reset();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Adding the section failed.');
    }
  };

  if (!open) {
    return (
      <div className="mt-8">
        <Button variant="outline" onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add section
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="template-add-section" className="mt-8 space-y-3 rounded-md border p-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="template-add-section-title">Title</Label>
        <Input
          id="template-add-section-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>
      {outlineBacked ? (
        <div className="flex flex-col gap-1.5">
          <Label>Source blocks to cover</Label>
          {uncovered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every source block is already covered or excluded.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {uncovered.map((block) => {
                const picked = pickedBlockIds.includes(block.id);
                return (
                  <button
                    key={block.id}
                    type="button"
                    onClick={() =>
                      setPickedBlockIds((previous) =>
                        picked ? previous.filter((id) => id !== block.id) : [...previous, block.id]
                      )
                    }
                    title={block.preview}
                  >
                    <Badge variant={picked ? 'default' : 'outline'}>
                      {block.kind} #{block.id}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="template-add-section-skeleton">Skeleton</Label>
          <Textarea
            id="template-add-section-skeleton"
            rows={5}
            value={skeleton}
            onChange={(event) => setSkeleton(event.target.value)}
            placeholder="The fixed scaffolding every instance starts from — boilerplate, labels with [placeholders], or a table header with its | --- | separator row"
          />
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="template-add-section-guidance">Guidance (one line)</Label>
        <Input
          id="template-add-section-guidance"
          value={guidance}
          onChange={(event) => setGuidance(event.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() => void handleSubmit()}
          disabled={
            addSection.isPending ||
            title.trim().length === 0 ||
            (outlineBacked ? pickedBlockIds.length === 0 : skeleton.trim().length === 0)
          }
        >
          {addSection.isPending ? 'Adding…' : 'Add section'}
        </Button>
        <Button variant="ghost" onClick={reset}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
